// DUAL-MODE CONTRACT FILE — this decoder is the shared wire for BOTH the air
// and naval instances. Naval flags (fire/flood/DC/aground/spotted/repair) are
// bit-packed into the vehicle row; every naval section is decoded
// unconditionally (air rows just leave the bits at zero). Any change here must
// land in the same commit as the matching internal/game/binsnap.go encode change
// plus a client/sw.js CACHE_VERSION bump, and be verified in BOTH theaters.
//
// Binary snapshot decoder — mirrors internal/game/binsnap.go. Kept
// dependency-free so the Node smoke test can import it too.

export const SNAPSHOT_MAGIC = 0xa1;
export const KILLCAM_MAGIC = 0xa2;

/**
 * Decode a binary snapshot into the object shape the client consumes:
 * { t:"s", tick, now, ack, phase, far,
 *   pl: [[id,x,y,z,qx,qy,qz,qw,vx,vy,vz,hp,status,thr,plane,bombs,ox,oy,oz,
 *        engine,wingL,wingR,fuel,pilot,tail], ...],
 *   bl: [[x,y,z,vx,vy,vz,team,kind,caliber,tracer], ...],
 *   bm: [[x,y,z,vx,vy,vz,team,shell,torpedo,flare,missile,rocket,chute], ...],
 *   aa: [[id,hp,yaw,pitch,firing,manned], ...],
 *   tk: [[id,x,y,z,yaw,hp,team], ...],
 *   ss: [[id,x,y,z,yaw,hp,team], ...] (supply ships),
 *   gv: [[id,type,x,y,z,yaw,turretYaw,turretPitch,hp,team,firing,onfire,flooding,dc,aground,spotted,repairing], ...],
 *   tu: [[gunner,host,seat,yaw,pitch,firing], ...] (manned bomber turrets),
 *   sh: [[id,hp,alive], ...],
 *   sl: [[yaw,pitch,on,locked], ...] (searchlights, welcome order),
 *   sc: {c,b,t,z,ph,w,wxi,windX,windZ,mdi} }
 */
export function decodeSnapshot(buffer) {
  const dv = new DataView(buffer);
  let off = 0;
  const u8 = () => dv.getUint8(off++);
  const u16 = () => {
    const v = dv.getUint16(off, true);
    off += 2;
    return v;
  };
  const u32 = () => {
    const v = dv.getUint32(off, true);
    off += 4;
    return v;
  };
  const i16 = () => {
    const v = dv.getInt16(off, true);
    off += 2;
    return v;
  };
  const f32 = () => {
    const v = dv.getFloat32(off, true);
    off += 4;
    return v;
  };

  if (u8() !== SNAPSHOT_MAGIC) throw new Error("bad snapshot magic");
  const tick = u32();
  const now = f32();
  const ack = u32();
  const phase = u8(); // ticks the acked input has already steered
  const far = (u8() & 1) !== 0;

  const pl = [];
  for (let n = u16(); n > 0; n--) {
    const id = u32();
    const x = f32(), y = f32(), z = f32();
    const qx = i16() / 32767, qy = i16() / 32767, qz = i16() / 32767, qw = i16() / 32767;
    const vx = i16() / 100, vy = i16() / 100, vz = i16() / 100;
    const ox = i16() / 1000, oy = i16() / 1000, oz = i16() / 1000;
    const hp = u8();
    const status = u8();
    const plane = u8();
    const thr = u8() / 255;
    const bombs = u8();
    // Localized-damage subsystem health (0..1 each).
    const engine = u8() / 255, wingL = u8() / 255, wingR = u8() / 255;
    const fuel = u8() / 255, pilot = u8() / 255, tail = u8() / 255;
    pl.push([id, x, y, z, qx, qy, qz, qw, vx, vy, vz, hp, status, thr, plane, bombs, ox, oy, oz,
      engine, wingL, wingR, fuel, pilot, tail]);
  }

  const bl = [];
  for (let n = u16(); n > 0; n--) {
    const x = f32(), y = f32(), z = f32();
    const vx = i16() / 20, vy = i16() / 20, vz = i16() / 20; // *20 wire scale: ±1638 m/s, clears a naval shell
    // flags: team | kind<<1 | tracer<<2 | caliber<<3
    const flags = u8();
    bl.push([x, y, z, vx, vy, vz, flags & 1, (flags >> 1) & 1, flags >> 3, (flags >> 2) & 1]);
  }

  const bm = [];
  for (let n = u16(); n > 0; n--) {
    const x = f32(), y = f32(), z = f32();
    const vx = i16() / 20, vy = i16() / 20, vz = i16() / 20; // *20 wire scale: ±1638 m/s, clears a naval shell
    // flags: team | shell<<1 | torpedo<<2 | flare<<3 | missile<<4 | rocket<<5 | chute<<6
    // (shells, fish, star-shell illuminants, land wire-guided missiles,
    // aircraft rockets and bailed-pilot parachutes all ride this section)
    const flags = u8();
    bm.push([x, y, z, vx, vy, vz, flags & 1, (flags >> 1) & 1, (flags >> 2) & 1, (flags >> 3) & 1, (flags >> 4) & 1, (flags >> 5) & 1, (flags >> 6) & 1]);
  }

  const aa = [];
  for (let n = u16(); n > 0; n--) {
    const id = 60000 + u16();
    const hp = u8();
    const yaw = i16() / 10000;
    const pitch = i16() / 10000;
    // flags: firing | manned<<1
    const flags = u8();
    aa.push([id, hp, yaw, pitch, flags & 1, (flags >> 1) & 1]);
  }

  const tk = [];
  for (let n = u16(); n > 0; n--) {
    const id = 80000 + u16();
    const x = f32(), y = f32(), z = f32();
    const yaw = i16() / 10000;
    const hp = u8();
    const team = u8();
    tk.push([id, x, y, z, yaw, hp, team]);
  }

  const ss = [];
  for (let n = u16(); n > 0; n--) {
    const id = 50000 + u16();
    const x = f32(), y = f32(), z = f32();
    const yaw = i16() / 10000;
    const hp = u8();
    const team = u8();
    ss.push([id, x, y, z, yaw, hp, team]);
  }

  const gv = [];
  for (let n = u16(); n > 0; n--) {
    const id = 90000 + u16();
    const type = u8();
    const x = f32(), y = f32(), z = f32();
    const yaw = i16() / 10000;
    const turretYaw = i16() / 10000;
    const turretPitch = i16() / 10000;
    const hp = u16(); // u16 (was u8): warship HP exceeds 255 (F4)
    // flags: team | firing<<1 | onfire<<2 | flooding<<3 | dcActive<<4 | aground<<5 | spotted<<6 | repairing<<7
    const flags = u8();
    gv.push([
      id, type, x, y, z, yaw, turretYaw, turretPitch, hp,
      flags & 1, (flags >> 1) & 1, (flags >> 2) & 1, (flags >> 3) & 1, (flags >> 4) & 1, (flags >> 5) & 1, (flags >> 6) & 1, (flags >> 7) & 1,
    ]);
  }

  const tu = [];
  for (let n = u16(); n > 0; n--) {
    const gunner = u32();
    const host = u32();
    const seat = u8();
    const yaw = i16() / 10000;
    const pitch = i16() / 10000;
    // flags: trigger held (drives the muzzle effect)
    const flags = u8();
    tu.push([gunner, host, seat, yaw, pitch, flags & 1]);
  }

  const sh = [];
  for (let n = u16(); n > 0; n--) {
    const id = 70000 + u16();
    const hp = u16();
    const flags = u8();
    sh.push([id, hp, flags & 1]);
  }

  const sl = [];
  for (let n = u16(); n > 0; n--) {
    const yaw = i16() / 10000;
    const pitch = i16() / 10000;
    const flags = u8();
    sl.push([yaw, pitch, flags & 1, (flags >> 1) & 1]);
  }

  let phByte;
  const sc = {
    c: u16(),
    b: u16(),
    t: u16(),
    z: (u8() << 24) >> 24, // sign-extend i8
    // Phase byte: 0 play, 1 end, 2 play with the OVERTIME finale latched.
    ph: (phByte = u8()) === 1 ? "end" : "play",
    w: (u8() << 24) >> 24,
    wxi: u8(),
    windX: (u8() << 24) >> 24, // round wind, m/s (bombs drift downwind)
    windZ: (u8() << 24) >> 24,
    mdi: u8(), // round type, index into config.match.modes
  };
  sc.ot = phByte === 2;

  // Naval smoke screens: [x, z, team, lifeFrac(0..1)] (empty in the air game).
  const sm = [];
  for (let n = u16(); n > 0; n--) {
    const x = f32(), z = f32();
    const team = u8();
    const life = u8() / 255;
    sm.push([x, z, team, life]);
  }

  // Domination zone holders (naval/land): a trailing OPTIONAL section the air
  // game omits entirely. Read only when bytes remain, so old air snapshots
  // still land exactly on the length check.
  if (off < buffer.byteLength) {
    const zs = [];
    for (let n = u8(); n > 0; n--) zs.push((u8() << 24) >> 24); // sign-extend i8
    sc.zs = zs;
  }

  // Combined-arms trailing section: every emplacement's CURRENT team, in the
  // same order as the aa rows above (Fort Brick's garrison changes flags
  // mid-round). Optional like the zone holders — only the armada emits it.
  let gt = null;
  if (off < buffer.byteLength) {
    gt = [];
    for (let n = u16(); n > 0; n--) gt.push(u8());
  }

  // Jets-era trailing section (era servers only): THIS recipient's seeker —
  // locked target id, lock progress 0..1 — plus rails and flares remaining.
  // Server-owned, so the lock ring the HUD draws is the ring the launch gate
  // checks. Optional like the sections above.
  let jt = null;
  if (off < buffer.byteLength) {
    jt = { lock: u32(), prog: u8() / 255, msl: u8(), fl: u8() };
  }

  if (off !== buffer.byteLength) {
    throw new Error(`snapshot length mismatch: ${off} != ${buffer.byteLength}`);
  }
  return { t: "s", tick, now, ack, phase, far, pl, bl, bm, aa, tk, ss, gv, tu, sh, sl, sc, sm, gt, jt };
}

/**
 * Decode a killcam message (internal/game/killcam.go): the last few seconds
 * of full snapshots, replayed client-side from the killer's point of view.
 * Returns { killer, victim, frames: [snapshot, ...] }.
 */
export function decodeKillcam(buffer) {
  const dv = new DataView(buffer);
  if (dv.getUint8(0) !== KILLCAM_MAGIC) throw new Error("bad killcam magic");
  const killer = dv.getUint32(1, true);
  const victim = dv.getUint32(5, true);
  const n = dv.getUint16(9, true);
  let off = 11;
  const frames = [];
  for (let i = 0; i < n; i++) {
    const len = dv.getUint32(off, true);
    off += 4;
    frames.push(decodeSnapshot(buffer.slice(off, off + len)));
    off += len;
  }
  if (off !== buffer.byteLength) {
    throw new Error(`killcam length mismatch: ${off} != ${buffer.byteLength}`);
  }
  return { killer, victim, frames };
}
